import useSWR from "swr"
import { useSmoothApi } from "src/hooks/useSmoothApi"
import { SMOOTH_LINE_API_URL } from "src/lib/constants"
import type {
  EvaluationCase,
  EvaluationRun,
  GeneratePrompt,
  ImportCustomer,
  ImportPreview,
  ImportSource,
  ImportTimeline,
  RunDetail,
  Terminals,
} from "./types"

const URL = `${SMOOTH_LINE_API_URL}/base_api/smoother/screen/dunning_evaluations`

/** sourceUsers は取り込み元のユーザー ID ごとのお客様の名前 */
export type EvaluationLibrary = {
  data: EvaluationCase[]
  terminals: Terminals
  sourceUsers?: Record<string, string | null>
}
export type EvaluationCaseDetail = { data: EvaluationCase; runs: EvaluationRun[] }

/** 保存 API に渡すのは編集できる項目だけ（id・latestRun・しまった記録は送らない） */
const casePayload = (record: EvaluationCase) => ({
  title: record.title,
  description: record.description,
  expectedTerminal: record.expectedTerminal,
  checkpoints: record.checkpoints,
  caseSet: record.caseSet ?? "",
  scenario: record.scenario,
  // 期待の形・カバレッジは持っているときだけ送る（送らなければサーバは今の値のまま）
  ...(record.expectation ? { expectation: record.expectation } : {}),
  ...(record.coverage ? { coverage: record.coverage } : {}),
})

export function useEvaluationSandbox(id?: number) {
  const { getWithToken, postWithToken, patchWithToken, deleteWithToken } = useSmoothApi()
  const library = useSWR<EvaluationLibrary>(
    URL,
    async (url: string) => (await getWithToken(url)).data,
    { revalidateOnFocus: false }
  )
  const detail = useSWR<EvaluationCaseDetail>(
    id ? `${URL}/${id}` : null,
    async (url: string) => (await getWithToken(url)).data,
    {
      revalidateOnFocus: false,
      refreshInterval: (data) =>
        data?.runs.some(
          (run) => run.status === "running" && Date.now() - Date.parse(run.createdAt) < 15 * 60_000
        )
          ? 5000
          : 0,
    }
  )

  return {
    library,
    detail,
    /** id が 0 なら新規作成、それ以外は lockVersion 付きで更新する */
    async save(record: EvaluationCase): Promise<EvaluationCase> {
      const response = record.id
        ? await patchWithToken(`${URL}/${record.id}`, {
            ...casePayload(record),
            lockVersion: record.lockVersion,
          })
        : await postWithToken(URL, {
            ...casePayload(record),
            // 流れ（元のケース）と取り込み元は作るときだけ決める
            ...(record.parentCaseId ? { parentCaseId: record.parentCaseId } : {}),
            ...(record.sourceDunningCaseId
              ? { sourceDunningCaseId: record.sourceDunningCaseId }
              : {}),
          })
      await Promise.all([library.mutate(), record.id ? detail.mutate() : undefined])
      return response.data.data
    },
    /** 督促ケース（ID またはユーザー ID、基準日時）か本番の生成リクエストから検証ケースの下書きを組み立てる。保存はしない */
    async importPreview(source: ImportSource): Promise<ImportPreview> {
      return (await postWithToken(`${URL}/import_preview`, source)).data.data
    },
    /** 読み込む時点を選ぶための、お客様の直近の会話と本番の生成リクエストの一覧 */
    async importTimeline(customer: ImportCustomer): Promise<ImportTimeline> {
      const query = new URLSearchParams(
        "userId" in customer
          ? { userId: String(customer.userId) }
          : { dunningCaseId: String(customer.dunningCaseId) }
      )
      return (await getWithToken(`${URL}/import_timeline?${query.toString()}`)).data.data
    },
    /** requestKey は呼び出し側が「1回の生成意図」ごとに持ち、失敗時の再送で使い回す */
    async generate(
      record: EvaluationCase,
      prompt: GeneratePrompt,
      requestKey: string
    ): Promise<EvaluationRun> {
      const response = await postWithToken(
        `${URL}/${record.id}/generate`,
        { ...prompt, lockVersion: record.lockVersion, requestKey },
        { timeout: 240_000 }
      )
      await Promise.all([detail.mutate(), library.mutate()])
      return response.data.data
    },
    async inspect(runId: number): Promise<RunDetail> {
      return (await getWithToken(`${URL}/runs/${runId}`)).data.data
    },
    /** しまう（archived: true）／戻す（false）。内容は変えないので版（lockVersion）は変わらない */
    async archive(record: EvaluationCase, archived: boolean): Promise<EvaluationCase> {
      const response = await postWithToken(
        `${URL}/${record.id}/${archived ? "archive" : "restore"}`,
        {}
      )
      await library.mutate()
      return response.data.data
    },
    /** 一覧で選んだケースをまとめてしまう（archived: true）／戻す（false） */
    async archiveMany(ids: number[], archived: boolean): Promise<void> {
      await postWithToken(`${URL}/archive`, { ids, archived })
      await library.mutate()
    },
    /**
     * 選んだケースをまとめて回帰テスト（case_set core）に入れる（included: true）／外す（false）。
     * 外したときの箱はサーバーが決める（SetDunningEvaluationCasesRegressionCommand）。しまったケースはサーバーが飛ばす。
     * 内容は変えないので版（lockVersion）は変わらない。扱ったケースを読み直した一覧から返す（読み直せなければ空）
     */
    async setRegression(ids: number[], included: boolean): Promise<EvaluationCase[]> {
      const response = await postWithToken(`${URL}/regression`, { ids, included })
      const handled = new Set<number>(response.data.data.ids)
      const refreshed = await library.mutate()
      return (refreshed?.data ?? []).filter((record) => handled.has(record.id))
    },
    /** 標準ケース以外（手作り・読み込んだケース）を生成結果ごと削除する（標準ケースと生成中・実行中のプロンプトの比較の対象は 422） */
    async remove(record: EvaluationCase): Promise<void> {
      await deleteWithToken(`${URL}/${record.id}`)
      await library.mutate()
    },
  }
}

export type EvaluationSandboxApi = ReturnType<typeof useEvaluationSandbox>
